// Taylor's map ("Plotting logic", v3 doc). Pure geometry, no React, so the PDF can reuse it.
//
// - A centre square split into four equal cells by a cross at the midline.
// - x = Clarity (left Unclear, right Clear); y = Freedom (bottom Bound, top Free).
// - The dot sits inside the square, inset by P (6% of the side) so 0 and 100 are not
//   drawn on the border:
//     x = left + P + clarity/100 * (S - 2P)
//     y = top  + P + (1 - freedom/100) * (S - 2P)
// - Corner arcs outside the square carry the domain: top-left Strategic, top-right
//   Relating, bottom-left Executing, bottom-right Influencing. The dot never moves
//   toward a corner.

import type { DomainCode, QuadrantName } from "./types";

export interface MapFrame {
  /** Full drawing size (square viewBox). */
  size: number;
  /** Top-left corner of the centre square. */
  left: number;
  top: number;
  /** Side of the centre square. */
  side: number;
  /** Dot inset inside the square. */
  pad: number;
  /** Radius of the corner arcs. */
  arcRadius: number;
}

/** Dot inset, as a percentage of the square side (Taylor: "about 6% of S"). */
export const INSET_PERCENT = 6;

export function mapFrame(side = 240, margin = 64): MapFrame {
  return {
    size: side + margin * 2,
    left: margin,
    top: margin,
    side,
    pad: (side * INSET_PERCENT) / 100,
    arcRadius: Math.round(side * 0.12),
  };
}

const clamp = (n: number) => Math.min(100, Math.max(0, Number.isFinite(n) ? n : 0));

/** Where the dot goes for these scores. */
export function plotPoint(clarity: number, freedom: number, f: MapFrame = mapFrame()): { x: number; y: number } {
  const span = f.side - 2 * f.pad;
  return {
    x: f.left + f.pad + (clamp(clarity) / 100) * span,
    y: f.top + f.pad + (1 - clamp(freedom) / 100) * span,
  };
}

/**
 * Thin guides from the dot to the square's left edge (its Freedom) and bottom edge (its
 * Clarity), so the reader can see where each score lands.
 */
export function guideLines(clarity: number, freedom: number, f: MapFrame = mapFrame()) {
  const p = plotPoint(clarity, freedom, f);
  return {
    toLeft: { x1: f.left, y1: p.y, x2: p.x, y2: p.y },
    toBottom: { x1: p.x, y1: p.y, x2: p.x, y2: f.top + f.side },
  };
}

export type CellPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export const QUADRANT_CELLS: Record<QuadrantName, CellPosition> = {
  Drifting: "top-left",
  Activated: "top-right",
  Fragmented: "bottom-left",
  Caged: "bottom-right",
};

export const DOMAIN_CORNERS: Record<DomainCode, CellPosition> = {
  STR: "top-left",
  REL: "top-right",
  EXE: "bottom-left",
  INF: "bottom-right",
};

export const DOMAIN_ORDER: DomainCode[] = ["STR", "REL", "EXE", "INF"];
export const QUADRANT_ORDER: QuadrantName[] = ["Drifting", "Activated", "Fragmented", "Caged"];

export const DEFAULT_DOMAIN_NAMES: Record<DomainCode, string> = {
  STR: "Strategic",
  REL: "Relating",
  EXE: "Executing",
  INF: "Influencing",
};

/** The rectangle of one quadrant cell (half the square each way). */
export function cellRect(position: CellPosition, f: MapFrame = mapFrame()) {
  const half = f.side / 2;
  const right = position.endsWith("right");
  const bottom = position.startsWith("bottom");
  return { x: f.left + (right ? half : 0), y: f.top + (bottom ? half : 0), width: half, height: half };
}

export const quadrantRect = (quadrant: QuadrantName, f: MapFrame = mapFrame()) => cellRect(QUADRANT_CELLS[quadrant], f);

/** The square's corner point for a cell position. */
export function cornerPoint(position: CellPosition, f: MapFrame = mapFrame()) {
  return {
    x: position.endsWith("right") ? f.left + f.side : f.left,
    y: position.startsWith("bottom") ? f.top + f.side : f.top,
  };
}

/**
 * A quarter-circle around the square's corner, on the outside: from the point beside
 * the corner (horizontally outward) to the point above or below it (vertically outward).
 */
export function arcPath(domain: DomainCode, f: MapFrame = mapFrame()): string {
  const position = DOMAIN_CORNERS[domain];
  const c = cornerPoint(position, f);
  const dx = position.endsWith("right") ? 1 : -1;
  const dy = position.startsWith("bottom") ? 1 : -1;
  const r = f.arcRadius;
  const sweep = dx * dy > 0 ? 1 : 0;
  return `M ${c.x + dx * r} ${c.y} A ${r} ${r} 0 0 ${sweep} ${c.x} ${c.y + dy * r}`;
}

/** Where a corner's label sits: a row beyond the arc, aligned to the outside edge. */
export function arcLabelPosition(domain: DomainCode, f: MapFrame = mapFrame()) {
  const position = DOMAIN_CORNERS[domain];
  const c = cornerPoint(position, f);
  const right = position.endsWith("right");
  const bottom = position.startsWith("bottom");
  return {
    x: c.x + (right ? f.arcRadius : -f.arcRadius),
    y: bottom ? c.y + f.arcRadius + 22 : c.y - f.arcRadius - 12,
    anchor: (right ? "end" : "start") as "start" | "end",
  };
}

export interface DomainRecord {
  primary_domain: DomainCode;
  secondary_domain: DomainCode;
  balanced: boolean;
  domain_counts: Record<DomainCode, number>;
}

/** Taylor: show the secondary arc unless the balanced flag is off and the primary leads by 4 or more. */
export function showSecondaryArc(r: DomainRecord): boolean {
  if (r.balanced) return true;
  const lead = (r.domain_counts[r.primary_domain] ?? 0) - (r.domain_counts[r.secondary_domain] ?? 0);
  return lead < 4;
}

export type ArcRole = "primary" | "secondary" | "other";

export function arcRole(domain: DomainCode, r: DomainRecord): ArcRole {
  if (domain === r.primary_domain) return "primary";
  if (domain === r.secondary_domain && showSecondaryArc(r)) return "secondary";
  return "other";
}

export interface Scores {
  freedom: number;
  clarity: number;
}

/**
 * "Freedom 27, Clarity 39: Fragmented. Your domain: Relating." With a before point:
 * "... Before: Freedom 44, Clarity 44, Fragmented."
 */
export function mapLabel(
  r: { freedom: number; clarity: number; quadrant: QuadrantName; primary_domain: DomainCode },
  domainNames: Partial<Record<DomainCode, string>> = DEFAULT_DOMAIN_NAMES,
  before?: (Scores & { quadrant?: QuadrantName }) | null,
): string {
  const domain = domainNames[r.primary_domain] ?? DEFAULT_DOMAIN_NAMES[r.primary_domain];
  const now = `Freedom ${r.freedom}, Clarity ${r.clarity}: ${r.quadrant}. Your domain: ${domain}.`;
  if (!before) return now;
  return `${now} Before: Freedom ${before.freedom}, Clarity ${before.clarity}${before.quadrant ? `, ${before.quadrant}` : ""}.`;
}

// ---------------------------------------------------------------- before / after (event series)

/** A signed change for display: "+5", "−17" (a real minus sign), "±0". */
export function formatChange(n: number): string {
  if (n > 0) return `+${n}`;
  if (n < 0) return `−${Math.abs(n)}`;
  return "±0";
}

/** "Freedom 44 → 27 (−17) · Clarity 44 → 39 (−5)" */
export function changeLine(before: Scores, after: Scores): string {
  const part = (label: string, a: number, b: number) => `${label} ${a} → ${b} (${formatChange(b - a)})`;
  return `${part("Freedom", before.freedom, after.freedom)} · ${part("Clarity", before.clarity, after.clarity)}`;
}

// ---------------------------------------------------------------- history (retakes)

/** At most this many earlier takes are drawn. */
export const HISTORY_MAX = 3;
const HISTORY_OPACITY = [0.75, 0.5, 0.3];

/**
 * Earlier takes as small muted dots: newest first and darkest, at most three. The input is
 * newest first (as the API sends it).
 */
export function historyDots<T extends Scores>(earlier: T[], f: MapFrame = mapFrame()) {
  return earlier.slice(0, HISTORY_MAX).map((item, i) => ({
    item,
    ...plotPoint(item.clarity, item.freedom, f),
    opacity: HISTORY_OPACITY[i] ?? 0.3,
  }));
}

/**
 * The line from the before dot to the after dot, trimmed by `gap` at both ends so it runs
 * between the two markers. Null when the dots are too close to draw a line between them.
 */
export function trailLine(before: Scores, after: Scores, f: MapFrame = mapFrame(), gap = 12) {
  const a = plotPoint(before.clarity, before.freedom, f);
  const b = plotPoint(after.clarity, after.freedom, f);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len <= gap * 2 + 4) return null;
  const ux = dx / len;
  const uy = dy / len;
  return { x1: a.x + ux * gap, y1: a.y + uy * gap, x2: b.x - ux * gap, y2: b.y - uy * gap };
}
